import { BugAntIcon } from '@heroicons/react/24/outline';
import { useQueries } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Loader } from '@/components/Loader';
import { getScanStatusConfig, ScanStatusModal } from '@/components/ScanStatus';
import { NoData } from '@/components/legacy-ui/NoData';
import { StatusChip } from '@/components/legacy-ui/StatusChip';
import {
  createAllEmptyConfig,
  NO_DATA_ICON_CLASS,
} from '@/components/legacy-ui/utils/createNoDataConfig';
import { useAxios } from '@/hooks/useAxios';
import { useCancelJob, useReRunJob } from '@/hooks/useJobs';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { Vulnerabilities } from '@/sections/vulnerabilities';
import { OsintProvider } from '@/state/OsintContext';
import { Job, JobStatus, Technology } from '@/types';
import { mergeJobStatus, useInfiniteQuery } from '@/utils/api';

interface TechnologyOsintProps {
  technology: Technology;
  onResearchClick?: () => void;
  isPraetorianUser?: boolean;
}

interface Repository {
  key: string;
  url: string;
}

export function TechnologyOsint({
  technology,
  onResearchClick,
  isPraetorianUser,
}: TechnologyOsintProps) {
  if (!isPraetorianUser) {
    return (
      <div className="flex size-full flex-col overflow-auto">
        <NoData
          {...createAllEmptyConfig({
            entityName: 'Technology Has Not Been Targeted for 0-Day Research',
            icon: <BugAntIcon className={NO_DATA_ICON_CLASS} />,
            description:
              'Use Constantine to find 0-day vulnerabilities in popular opensource technologies.',
            actionLabel: 'Summon Constantine',
          })}
          action={
            <Button
              size="sm"
              disabled
            >
              Summon Constantine
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <OsintProvider>
      <TechnologyOsintContent
        technology={technology}
        onResearchClick={onResearchClick}
      />
    </OsintProvider>
  );
}

function TechnologyOsintContent({
  technology,
  onResearchClick,
}: Omit<TechnologyOsintProps, 'isPraetorianUser'>) {
  const axios = useAxios();
  const [expandedRepos, setExpandedRepos] = useState<Set<string>>(new Set());
  const [openModalRepoUrl, setOpenModalRepoUrl] = useState<string | null>(null);
  const { mutateAsync: reRunJob } = useReRunJob();
  const { mutateAsync: cancelJob } = useCancelJob();

  // Query OSINT partition: Technology -[:HAS_REPOSITORY]-> Repository
  const { data: reposResponse, status: reposStatus } = useInfiniteQuery<
    Repository[]
  >({
    queryKey: ['OSINT', 'repos', 'technology', technology.key],
    defaultErrorMessage: 'Failed to fetch exploit repositories',
    queryFn: async () => {
      const { data } = await axios({
        method: 'POST',
        url: '/my',
        params: {
          label: 'asset',
          osint: 'true',
          tree: true,
        },
        data: {
          node: {
            labels: ['Technology'],
            filters: [
              {
                field: 'key',
                operator: '=',
                value: technology.key,
              },
            ],
            relationships: [
              {
                label: 'HAS_REPOSITORY',
                target: {
                  labels: ['Repository'],
                },
              },
            ],
          },
        },
      });

      const repos: Repository[] = [];
      const items = Array.isArray(data) ? data : [];
      for (const item of items) {
        const targetResult = item?.node?.relationships?.[0]?.target?.result;
        const repoResults =
          targetResult?.repositories || targetResult?.assets || [];
        for (const repo of repoResults) {
          if (repo.url) {
            repos.push({ key: repo.key, url: repo.url });
          }
        }
      }

      return {
        data: repos,
        offset: undefined,
      };
    },
    initialPageParam: undefined,
    getNextPageParam: () => undefined,
  });

  const repos = useMemo(() => {
    if (!reposResponse) return [];
    return reposResponse.pages.flatMap(page => page.data);
  }, [reposResponse]);

  // Batch-fetch jobs for all repositories using useQueries
  const jobQueries = useQueries({
    queries: repos.map(repo => ({
      queryKey: [...getQueryKey.getMy('job', ''), 'osint', repo.url],
      queryFn: async () => {
        const { data } = await axios.get('/my', {
          params: {
            label: 'job',
            key: `#job#${repo.url}`,
            osint: 'true',
          },
        });
        return data?.jobs || [];
      },
      enabled: repos.length > 0,
    })),
  });

  const reposWithJobs = useMemo(() => {
    return repos.map((repo, index) => ({
      ...repo,
      jobs: (jobQueries[index]?.data as Job[]) || [],
      jobsStatus: jobQueries[index]?.status,
    }));
  }, [repos, jobQueries]);

  const toggleRepo = (repoUrl: string) => {
    setExpandedRepos(prev => {
      const next = new Set(prev);
      if (next.has(repoUrl)) {
        next.delete(repoUrl);
      } else {
        next.add(repoUrl);
      }
      return next;
    });
  };

  if (reposStatus === 'pending') {
    return (
      <div className="flex h-48 items-center justify-center">
        <Loader isLoading type="spinner" />
      </div>
    );
  }

  if (repos.length === 0) {
    return (
      <div className="flex size-full flex-col overflow-auto">
        <NoData
          {...createAllEmptyConfig({
            entityName: 'Technology Has Not Been Targeted for 0-Day Research',
            icon: <BugAntIcon className={NO_DATA_ICON_CLASS} />,
            description:
              'Use Constantine to find 0-day vulnerabilities in popular opensource technologies.',
            onGetStarted: onResearchClick,
            actionLabel: 'Summon Constantine',
          })}
        />
      </div>
    );
  }

  const isSingleRepo = repos.length === 1;

  if (isSingleRepo) {
    const repo = reposWithJobs[0];
    const jobs = repo.jobs;
    const scanStatus = jobs.length
      ? mergeJobStatus(jobs.map(job => job.status.slice(0, 2) as JobStatus))
      : undefined;
    const statusConfig = getScanStatusConfig(scanStatus);

    return (
      <div className="flex h-full flex-col gap-4">
        {/* Inline repo header — no accordion */}
        <div className="flex items-center gap-3">
          <BugAntIcon className="text-chariot-text-secondary size-4 shrink-0" />
          <span className="text-chariot-text-primary truncate text-sm font-medium">
            {repo.url}
          </span>
          {repo.jobsStatus === 'pending' ? (
            <span className="flex items-center gap-1.5 text-xs">
              <Loader isLoading type="spinner" />
              <span className="text-chariot-text-secondary">
                Loading scan status...
              </span>
            </span>
          ) : statusConfig ? (
            <button
              onClick={() => setOpenModalRepoUrl(repo.url)}
              className="transition-opacity hover:opacity-80"
              aria-label="View scan details"
            >
              <StatusChip {...statusConfig} />
            </button>
          ) : (
            <span className="text-chariot-text-tertiary text-xs">No scans</span>
          )}
        </div>

        {/* Vulnerabilities rendered directly — no accordion wrapper */}
        <Vulnerabilities
          name={`osint-repo-${repo.key}`}
          inDrawer
          hideTitle
          parentFilters={{ assetKey: repo.key }}
          drawerFilters={['status', 'severity', 'origin']}
          emptyStateIcon={<BugAntIcon className={NO_DATA_ICON_CLASS} />}
        />

        {/* Scan Status Modal */}
        {(() => {
          if (!openModalRepoUrl) return null;
          const actionHandlers = {
            handleRerun: (jobKey: string) => reRunJob({ jobKey }),
            handleCancel: (jobKey: string) => cancelJob({ jobKey }),
          };
          return (
            <ScanStatusModal
              open={!!openModalRepoUrl}
              onClose={() => setOpenModalRepoUrl(null)}
              title={`${repo.url} Scans`}
              jobs={repo.jobs}
              isLoading={repo.jobsStatus === 'pending'}
              error={null}
              refetch={() => {}}
              actionHandlers={actionHandlers}
            />
          );
        })()}
      </div>
    );
  }

  // Multi-repo case: keep accordion pattern with BugAntIcon consistently
  return (
    <div className="flex h-full flex-col gap-4">
      <div className="mt-3 space-y-2">
        {reposWithJobs.map(repo => {
          const isExpanded = expandedRepos.has(repo.url);
          const jobs = repo.jobs;

          const scanStatus = jobs.length
            ? mergeJobStatus(
                jobs.map(job => job.status.slice(0, 2) as JobStatus)
              )
            : undefined;

          const statusConfig = getScanStatusConfig(scanStatus);

          return (
            <div
              key={repo.url}
              className="border-chariot-stroke rounded-md border"
            >
              <button
                onClick={() => toggleRepo(repo.url)}
                className="text-chariot-text-primary hover:bg-chariot-background-secondary flex w-full items-center justify-between p-3 text-left transition-colors"
              >
                <div className="flex items-center gap-3">
                  <BugAntIcon className="text-chariot-text-secondary size-4 shrink-0" />
                  <div>
                    <span className="text-chariot-text-primary text-sm font-medium">
                      {repo.url}
                    </span>
                    <div className="text-chariot-text-secondary mt-0.5 flex items-center gap-3 text-xs">
                      {repo.jobsStatus === 'pending' ? (
                        <span className="flex items-center gap-1.5">
                          <Loader isLoading type="spinner" />
                          <span>Loading scan status...</span>
                        </span>
                      ) : statusConfig ? (
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            setOpenModalRepoUrl(repo.url);
                          }}
                          className="transition-opacity hover:opacity-80"
                          aria-label="View scan details"
                        >
                          <StatusChip {...statusConfig} />
                        </button>
                      ) : (
                        <span className="text-chariot-text-tertiary">
                          No scans
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>

              {isExpanded && (
                <div className="border-chariot-stroke border-t p-4">
                  <Vulnerabilities
                    name={`osint-repo-${repo.key}`}
                    inDrawer
                    hideTitle
                    parentFilters={{ assetKey: repo.key }}
                    drawerFilters={['status', 'severity', 'origin']}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Scan Status Modal */}
      {(() => {
        const modalRepo = reposWithJobs.find(r => r.url === openModalRepoUrl);
        if (!modalRepo) return null;

        const actionHandlers = {
          handleRerun: (jobKey: string) => reRunJob({ jobKey }),
          handleCancel: (jobKey: string) => cancelJob({ jobKey }),
        };

        return (
          <ScanStatusModal
            open={!!openModalRepoUrl}
            onClose={() => setOpenModalRepoUrl(null)}
            title={`${modalRepo.url} Scans`}
            jobs={modalRepo.jobs}
            isLoading={modalRepo.jobsStatus === 'pending'}
            error={null}
            refetch={() => {}}
            actionHandlers={actionHandlers}
          />
        );
      })()}
    </div>
  );
}
